import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import { QueryConfig } from '../client';
import env from '../../config/env';
import {
  VehicleModelsByMakeResponse,
  VehicleModel,
} from '../types/VehicleModels';

interface RequestParams {
  make: string;
  companyId?: string;
}

const getVehicleModelsRequest = async (
  token: string,
  options: RequestParams
): Promise<VehicleModel[]> => {
  try {
    console.log('Fetching vehicle models from API for make:', options.make);
    const response = await fetch(`${env.apiUrl}/vehicle-models/makes/${encodeURIComponent(options.make)}/models`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json() as VehicleModelsByMakeResponse;
    console.log('Vehicle models API response:', data);
    // Ensure we always return an array, even if the response is malformed
    return Array.isArray(data.models) ? data.models : [];
  } catch (error) {
    console.error('Error fetching vehicle models:', error);
    // Return an empty array on error instead of throwing
    return [];
  }
};

export const useGetVehicleModels = (
  params: RequestParams,
  config?: QueryConfig<typeof getVehicleModelsRequest>
) => {
  const { getAccessTokenSilently } = useHybridAuth();
  
  return useQuery({
    ...config,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getVehicleModelsRequest(token, params);
    },
    queryKey: ['vehicle-models', params.make, params.companyId],
    enabled: !!params.make, // Only run query if make is provided
    staleTime: 1000 * 60 * 60,      // 1 hour - data stays fresh for 1 hour
    gcTime: 1000 * 60 * 60,         // 1 hour - cache retained for 1 hour after going stale
    retry: 1,                        // Reduce retry attempts from default 3 to 1
    retryDelay: (attemptIndex) => Math.min(1000 * (2 ** attemptIndex), 5000), // Cap at 5 seconds
  });
};
